import React, { useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { XIcon } from 'lucide-react'
import { createString } from '../api/strings'
import { redirectIfSessionExpired } from '../utils/sessionAuth'
import './RegisterPage.css'
import './NewStringModal.css'

interface NewStringModalProps {
  onClose: () => void
  onPosted?: () => void
}

const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
}

const sheetVariants = {
  hidden: { y: '100%', opacity: 0.6 },
  visible: { y: 0, opacity: 1 },
  exit: { y: '100%', opacity: 0 },
}

export function NewStringModal({ onClose, onPosted }: NewStringModalProps) {
  const navigate = useNavigate()
  const [draft, setDraft] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const handlePost = async () => {
    const trimmed = draft.trim()
    if (!trimmed || submitting) return

    setSubmitting(true)
    setError('')
    try {
      await createString(trimmed)
      onPosted?.()
      onClose()
    } catch (err) {
      if (redirectIfSessionExpired(navigate, err, { onClose })) return
      setError(
        err instanceof Error ? err.message : 'Could not post string. Please try again.',
      )
    } finally {
      setSubmitting(false)
    }
  }

  const modal = (
    <motion.div
      className="fixed inset-0 z-[200] flex flex-col justify-end"
      variants={backdropVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      transition={{ duration: 0.22 }}
    >
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      <motion.div
        className="relative z-10 w-full max-w-lg mx-auto bg-[#0d0d0d]/95 backdrop-blur-2xl border border-white/10 rounded-t-3xl flex flex-col max-h-[min(88vh,100dvh)]"
        variants={sheetVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        transition={{ type: 'spring', stiffness: 340, damping: 38, mass: 0.9 }}
      >
        <div className="flex justify-center pt-3 pb-1 shrink-0">
          <div className="w-10 h-1 rounded-full bg-white/20" />
        </div>

        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-white/10 transition-colors"
          aria-label="Close"
        >
          <XIcon className="w-4 h-4 text-white/50" />
        </button>

        <div className="px-6 pt-4 pb-6 flex flex-col flex-1 min-h-0 overflow-hidden">
          <p className="text-xs tracking-widest uppercase text-white/50 mb-2 shrink-0">
            Compose
          </p>
          <h2 className="text-lg md:text-xl text-white tracking-wide uppercase mb-4 shrink-0">
            NEW STRING
          </h2>

          <motion.div
            className="new-string-compose flex-1 min-h-0"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05, duration: 0.25 }}
          >
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Write something raw…"
              rows={4}
              className="new-string-textarea"
            />

            <motion.button
              type="button"
              onClick={handlePost}
              whileTap={{ scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 400, damping: 22 }}
              disabled={!draft.trim() || submitting}
              className="register-submit-btn new-string-submit"
            >
              {submitting ? 'Posting…' : 'Post string'}
            </motion.button>

            {error && (
              <p className="register-error-msg">{error}</p>
            )}
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  )

  return createPortal(modal, document.body)
}
